import { withRouter } from 'react-router-dom';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import isUndefined from 'lodash/isUndefined';
import { FormLabel, Grid, Paper } from '@mui/material';
import MUIDataTable from 'mui-datatables';
import React from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import reportActions from '../../../actions/reports';
import WrappedSpinner from '../../common/wrapped-spinner';
import { BrandSelect } from '../../common/brand-select';
import RepairTicketStatusSelect from '../../common/repair-ticket-status-select';
import CategoryErrorCodeSelect from '../../common/category-error-code-select';
import DateRangeDialog from '../../common/date-range-dialog';
import { MarketMultiSelect } from '../../common/market-multi-select';
import withPersistentSettings from '../../common/table-with-persistent-settings';
import saveLocalSettingsWrapper from './save-report-settings';
import { CategoryOrErrorCode, RepairTicketStatuses } from '../../../lib/utils';

const LOCAL_STORAGE_KEY = 'repair-by-category-page';
const styles = {
  root: {
    width: '95%',
    margin: '0 auto',
    overflowX: 'auto',
  },
};

function getMarketNames(markets, marketIds) {
  const marketNames = [];
  if (!isUndefined(marketIds) && marketIds.length > 0 && !isUndefined(markets)) {
    for (const market of markets) {
      if (marketIds.indexOf(market._id) > -1) {
        marketNames.push(market.name);
      }
    }
  } else if (!isUndefined(markets)) {
    markets.map((market) => marketNames.push(market.name));
  }
  return marketNames;
}

/*
 * Format repair ticket data into market and category aggregations (instead of mongo aggregation):
 * Check that we have tickets and selectedCategoryOrErrorCode, return null if we do not.
 * If we do, determine grouping based on Category or Error Code setting.
 * Find market names, count the number of tickets in each category associated with each market name.
 * Create a table with the category, count, count, etc. in each row
 */

function formatTableData(tickets, markets, marketIds, selectedCategoryOrErrorCode) {
  const formatedData = [];

  if (tickets.length < 1 || isUndefined(selectedCategoryOrErrorCode)) {
    return formatedData;
  } else {
    let ticketGroups;

    if (selectedCategoryOrErrorCode === CategoryOrErrorCode.ERROR_CODE) {
      const filteredTickets = tickets.filter(
        (ticket) => ticket.category === CategoryOrErrorCode.ERROR_CODE,
      );
      ticketGroups = groupBy(filteredTickets, (filteredTicket) => filteredTicket.errorCode);
    } else if (selectedCategoryOrErrorCode === CategoryOrErrorCode.CATEGORY) {
      ticketGroups = groupBy(tickets, (ticket) => ticket.category);
    }

    const groupKeys = Object.keys(ticketGroups);
    groupKeys.sort();
    const marketNames = getMarketNames(markets, marketIds);

    for (const category of groupKeys) {
      const ticketGroupRow = [category];
      const ticketGroupData = ticketGroups[category];

      for (const marketName of marketNames) {
        const myMarketTickets = ticketGroupData.filter(
          (ticket) => isUndefined(ticket.market) === false && ticket.market.name === marketName,
        );
        ticketGroupRow.push(myMarketTickets.length);
      }

      formatedData.push(ticketGroupRow);
    }

    return formatedData;
  }
}

function getColumnNames(repairTickets, markets, marketIds, selectedCategoryOrErrorCode) {
  const columnNames = [{
    name: selectedCategoryOrErrorCode === CategoryOrErrorCode.ERROR_CODE
      ? CategoryOrErrorCode.ERROR_CODE
      : CategoryOrErrorCode.CATEGORY,
  }];
  if (repairTickets.length > 0) {
    const marketNames = getMarketNames(markets, marketIds);
    const shortMarketNames = marketNames.map((name) => (name.length >= 15 ? `${name.slice(0, 15)}...` : name));
    shortMarketNames.map((name) => columnNames.push({ name }));
    return columnNames;
  }
}

class BaseRepairsByCategoryPage extends React.Component {
  constructor(props) {
    super(props);

    const {
      selectedBrand, marketIds, startDate, endDate,
    } = props;

    this.state = {
      selectedBrand,
      selectedStatus: RepairTicketStatuses.OPEN,
      marketIds,
      startDate: startDate ? moment.utc(startDate) : moment.utc(),
      endDate: endDate ? moment.utc(endDate) : moment.utc(),
      selectedCategoryOrErrorCode: CategoryOrErrorCode.CATEGORY,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleBrandChange = this.handleBrandChange.bind(this);
    this.handleStatusChange = this.handleStatusChange.bind(this);
    this.handleMarketChange = this.handleMarketChange.bind(this);
    this.handleDateChange = this.handleDateChange.bind(this);
    this.handleCategoryOrErrorCodeChange = this.handleCategoryOrErrorCodeChange.bind(this);
  }

  componentDidMount() {
    const { fetchRepairTicketsForAggregationReport } = this.props;
    const {
      startDate, endDate, selectedBrand, selectedStatus, marketIds,
    } = this.state;

    const query = {};
    if (startDate || endDate) {
      query.start_date = moment(startDate).format('x');
      query.end_date = moment(endDate).format('x');
    }
    if (selectedBrand) query.brand = selectedBrand;
    if (selectedStatus) query.status = selectedStatus;
    if (marketIds) query.market_id = marketIds;
    fetchRepairTicketsForAggregationReport(query);
  }

  async componentDidUpdate(prevProps, prevState) {
    const { fetchRepairTicketsForAggregationReport, saveReportSettings } = this.props;
    const {
      startDate, endDate, selectedBrand, selectedStatus, marketIds, selectedCategoryOrErrorCode,
    } = this.state;

    const isStartChanged = !moment(startDate).isSame(moment(prevState.startDate));
    const isEndChanged = !moment(endDate).isSame(moment(prevState.endDate));
    const isBrandChanged = selectedBrand !== prevState.selectedBrand;
    const isStatusChanged = selectedStatus !== prevState.selectedStatus;
    const isMarketChanged = marketIds !== prevState.marketIds;
    const isCategoryOrErrorCodeChanged = (
      selectedCategoryOrErrorCode !== prevState.selectedCategoryOrErrorCode
    );

    // any state different, then re-fetch
    if (isStartChanged
        || isEndChanged
        || isStatusChanged
        || isBrandChanged
        || isMarketChanged
        || isCategoryOrErrorCodeChanged) {
      const newSettings = {
        startDate: startDate.format(),
        endDate: endDate.format(),
        selectedBrand,
        selectedStatus,
        isCategoryOrErrorCodeChanged,
        marketIds,
      };
      saveReportSettings(newSettings);

      const skipFetch = !marketIds || marketIds.length === 0;
      if (skipFetch) return; // don't fetch without marketId

      const query = {};
      if (startDate || endDate) {
        query.start_date = moment(startDate).format('x');
        query.end_date = moment(endDate).format('x');
      }
      if (selectedBrand) query.brand = selectedBrand;
      if (selectedStatus) query.status = selectedStatus;
      if (marketIds) query.market_id = marketIds;

      await fetchRepairTicketsForAggregationReport(query);
    }
  }

  get tableOptions() {
    const basicOptions = get(this, 'props.tableOptions', {});
    const localOptions = {
      rowsPerPage: 100,
      rowsPerPageOptions: [20, 50, 100],
    };

    return { ...basicOptions, ...localOptions };
  }

  handleChange(event) {
    this.setState({ [event.target.name]: event.target.value });
  }

  handleDateChange(startDate, endDate) {
    this.setState({ startDate, endDate });
  }

  handleMarketChange(marketIds) {
    this.setState({ marketIds });
  }

  handleBrandChange(brand) {
    this.setState({ selectedBrand: brand });
  }

  handleStatusChange(status) {
    this.setState({ selectedStatus: status });
  }

  handleCategoryOrErrorCodeChange(categoryOrErrorCode) {
    this.setState({ selectedCategoryOrErrorCode: categoryOrErrorCode });
  }

  render() {
    const {
      marketsLoading, reportsLoading, repairTickets, markets,
    } = this.props;
    const {
      startDate, endDate, selectedBrand, selectedStatus, marketIds, selectedCategoryOrErrorCode,
    } = this.state;
    const isLoading = marketsLoading || reportsLoading;
    const displayData = formatTableData(
      repairTickets,
      markets,
      marketIds,
      selectedCategoryOrErrorCode,
    );
    const columnNames = getColumnNames(
      repairTickets,
      markets,
      marketIds,
      selectedCategoryOrErrorCode,
    );

    return (
      <WrappedSpinner isLoading={isLoading}>
        <Paper style={styles.root} sx={{ p: 2 }}>
          <Grid container>
            <Grid item xs={2} sx={{ mr: 2 }}>
              <MarketMultiSelect value={marketIds} handleChange={this.handleMarketChange} />
            </Grid>
            <Grid item xs={1} sx={{ mr: 1, mt: -1 }}>
              <BrandSelect value={selectedBrand} handleChange={this.handleBrandChange} />
            </Grid>
            <Grid item xs={1} sx={{ mr: 1, mt: -1 }}>
              <RepairTicketStatusSelect
                value={selectedStatus}
                handleChange={this.handleStatusChange}
              />
            </Grid>
            <Grid item xs={2} sx={{ mr: 2, mt: -1 }}>
              <CategoryErrorCodeSelect
                value={selectedCategoryOrErrorCode}
                handleChange={this.handleCategoryOrErrorCodeChange}
              />
            </Grid>
            <Grid item xs={1}>
              <FormLabel component="legend">Start</FormLabel>
              {startDate && startDate.format('MM-DD-YYYY')}
            </Grid>
            <Grid item xs={1}>
              <FormLabel component="legend">End</FormLabel>
              {endDate && endDate.format('MM-DD-YYYY')}
            </Grid>
            <Grid item xs={2}>
              <DateRangeDialog
                to={endDate}
                from={startDate}
                handleDateChange={this.handleDateChange}
              />
            </Grid>
            <Grid item xs={12}>
              {repairTickets && (
                <MUIDataTable
                  title={selectedCategoryOrErrorCode === CategoryOrErrorCode.ERROR_CODE
                    ? 'Repairs By Error Code' : 'Repairs by Category'}
                  columns={columnNames}
                  data={displayData}
                  options={this.tableOptions}
                />
              )}
            </Grid>
          </Grid>
        </Paper>
      </WrappedSpinner>
    );
  }
}

const mapStateToProps = ({ markets, reports }) => ({
  reportsLoading: reports.isLoading,
  repairTickets: get(reports, 'repairTicketsForAggregation', []),
  markets: markets.activeMarkets,
  marketsLoading: markets.isLoading,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  fetchRepairTicketsForAggregationReport: reportActions.fetchRepairTicketsForAggregationReport,
}, dispatch);

const RepairsByCategoryPage = saveLocalSettingsWrapper(
  withPersistentSettings(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(withRouter(BaseRepairsByCategoryPage)),
    LOCAL_STORAGE_KEY,
  ),
  LOCAL_STORAGE_KEY,
);

export { RepairsByCategoryPage, BaseRepairsByCategoryPage };
